import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PriceAlerts = ({ cryptoList }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      crypto: 'BTC',
      condition: 'above',
      price: 45000,
      active: true
    },
    {
      id: 2,
      crypto: 'ETH',
      condition: 'below',
      price: 2200,
      active: true
    }
  ]);

  const [newAlert, setNewAlert] = useState({
    crypto: 'BTC',
    condition: 'above',
    price: ''
  });

  const conditionOptions = [
    { value: 'above', label: 'Price goes above' },
    { value: 'below', label: 'Price goes below' }
  ];

  const cryptoOptions = cryptoList?.map(crypto => ({
    value: crypto?.symbol,
    label: `${crypto?.name} (${crypto?.symbol})`
  }));

  const handleAddAlert = (e) => {
    e?.preventDefault();
    if (newAlert?.price) {
      setAlerts([
        ...alerts,
        {
          id: Date.now(),
          crypto: newAlert?.crypto,
          condition: newAlert?.condition,
          price: parseFloat(newAlert?.price),
          active: true
        }
      ]);
      setNewAlert({ crypto: 'BTC', condition: 'above', price: '' });
      setIsOpen(false);
    }
  };

  const handleToggleAlert = (id) => {
    setAlerts(alerts?.map(alert =>
      alert?.id === id ? { ...alert, active: !alert?.active } : alert
    ));
  };

  const handleDeleteAlert = (id) => {
    setAlerts(alerts?.filter(alert => alert?.id !== id));
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={20} color="var(--color-primary)" />
            <h3 className="text-base md:text-lg font-semibold text-foreground">Price Alerts</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsOpen(true)}
          >
            Add Alert
          </Button>
        </div>

        {alerts?.length > 0 ? (
          <div className="space-y-3">
            {alerts?.map((alert) => (
              <div
                key={alert?.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <button
                    onClick={() => handleToggleAlert(alert?.id)}
                    className={`flex-shrink-0 w-10 h-6 rounded-full transition-colors ${
                      alert?.active ? 'bg-success' : 'bg-muted'
                    }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transition-transform ${
                        alert?.active ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {alert?.crypto} {alert?.condition === 'above' ? '>' : '<'} ${alert?.price?.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Alert when price goes {alert?.condition} ${alert?.price?.toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteAlert(alert?.id)}
                  className="flex-shrink-0 text-error hover:text-error/80 transition-colors ml-2"
                  aria-label="Delete alert"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="BellOff" size={48} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground mt-3">No price alerts set</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create alerts to get notified when prices reach your target
            </p>
          </div>
        )}
      </div>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Create Price Alert</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleAddAlert} className="space-y-4">
              <Select
                label="Cryptocurrency"
                options={cryptoOptions}
                value={newAlert?.crypto}
                onChange={(value) => setNewAlert({ ...newAlert, crypto: value })}
              />

              <Select
                label="Condition"
                options={conditionOptions}
                value={newAlert?.condition}
                onChange={(value) => setNewAlert({ ...newAlert, condition: value })}
              />

              <Input
                label="Target Price (USD)"
                type="number"
                placeholder="Enter target price"
                value={newAlert?.price}
                onChange={(e) => setNewAlert({ ...newAlert, price: e?.target?.value })}
                required
              />

              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  You will receive a notification when the price reaches your target. 
                  Alerts are checked every minute and will be automatically disabled after triggering.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  disabled={!newAlert?.price}
                >
                  Create Alert
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PriceAlerts;